import { useEffect, useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { emberApi } from '../api';
import { useRoute } from '../router';
import { KNOCKS_CHANGED_EVENT } from './use-knocks';

/**
 * Unread badge (§4.14): each mount shows exactly ONE notification family —
 * `new-embers` (unread new Embers on Requests the user follows; the Your
 * questions tab) or `knocks` (letters about an Ember waiting on the person,
 * §4.9 — the Embers tab, whose cards name them; 2026-09-06 拍板; since
 * 2026-09-25 the reader's `mattered` counts here too, the Mattered tab that
 * held it having folded into Embers) — or `notebook`, what waits in the
 * notebook, the sum of its tabs (knocks + Ember's weekly letters), on the
 * notebook's door (2026-10-21; until then a `total` on the profile icon that
 * also counted the rooms, which are the Chats door's since). Hidden at 0 and for
 * anonymous visitors. Refreshes on navigation so read actions are reflected
 * without a reload, and whenever a letter is answered on the page it is on
 * (KNOCKS_CHANGED_EVENT, 2026-09-08): the number must never outlive the
 * letters it counted.
 *
 * All skins render off this one component and the one summary endpoint: a
 * second implementation would be a second source of truth for the same fact.
 */
export function NotificationBadge({
  className = 'ember-nav-badge',
  kind = 'new-embers',
}: { className?: string; kind?: 'new-embers' | 'knocks' | 'letters' | 'notebook' } = {}): ReactElement | null {
  const { status } = useSession();
  const { path } = useRoute();
  const [count, setCount] = useState(0);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((n) => n + 1);
    window.addEventListener(KNOCKS_CHANGED_EVENT, bump);
    return () => window.removeEventListener(KNOCKS_CHANGED_EVENT, bump);
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') {
      setCount(0);
      return undefined;
    }
    let alive = true;
    emberApi.notificationSummary()
      .then((s) => {
        if (!alive) return;
        // v4.0 (2026-09-05): the top badge counts only what another person
        // did — a note that this mattered, and (2026-09-06) a letter waiting:
        // the share ask to answer or a note on a read. Unread Embers of
        // one's own are not a red number; the notebook lists them.
        const knocks = s.pending_knock_count || 0;
        // Ember's weekly letters not yet opened in the notebook (§4.20,
        // 2026-09-23): the Letters tab; the notebook's count adds them.
        // Questions are not (2026-10-20: Ask people stopped 2026-09-26).
        const letters = s.unread_letter_count || 0;
        setCount(kind === 'notebook' ? knocks + letters
            : kind === 'knocks' ? knocks
              : kind === 'letters' ? letters
                : s.unread_new_ember_count);
      })
      .catch(() => { /* badge is best-effort; never block nav */ });
    return () => { alive = false; };
  }, [status, path, kind, tick]);

  if (count <= 0) return null;
  const label = kind === 'knocks'
      ? `${count} letters waiting for you`
      : kind === 'letters'
        ? `${count} unread letters from Ember`
        : kind === 'notebook'
          ? `${count} unread notes and letters`
        : `${count} unread new Embers on your Requests`;
  return (
    <span className={className} aria-label={label}>
      {count > 99 ? '99+' : count}
    </span>
  );
}
